import { useEffect } from "react";
import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Loader2, X } from "lucide-react";

type Tone = "danger" | "success" | "neutral";

type Props = {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
  busy?: boolean;
  /** When true, the confirm button is the default action and gets focus. */
  autoFocusConfirm?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const TONE_ICON = {
  danger: AlertTriangle,
  success: CheckCircle2,
  neutral: AlertTriangle,
} as const;

/**
 * Lightweight confirmation popup used by the admin page for approve and
 * delete flows. Matches the existing `modal-backdrop` / `modal` pattern so
 * focus, scroll-lock, and dismiss behavior are consistent with the rest of
 * the app.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "neutral",
  busy = false,
  autoFocusConfirm = true,
  onConfirm,
  onCancel,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  const Icon = TONE_ICON[tone];
  const confirmClass =
    tone === "danger"
      ? "btn btn-danger"
      : tone === "success"
      ? "btn btn-primary"
      : "btn btn-primary";

  return (
    <div
      className="modal-backdrop confirm-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className={`modal confirm-modal confirm-${tone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <div className="confirm-head">
          <div className={`confirm-icon confirm-icon-${tone}`} aria-hidden>
            <Icon size={22} />
          </div>
          <div className="confirm-head-text">
            <h3 id="confirm-title" className="confirm-title">
              {title}
            </h3>
            <div className="confirm-message">{message}</div>
          </div>
          {!busy ? (
            <button
              type="button"
              className="modal-close"
              onClick={onCancel}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          ) : null}
        </div>
        <div className="modal-foot confirm-foot">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={confirmClass}
            onClick={onConfirm}
            disabled={busy}
            autoFocus={autoFocusConfirm}
          >
            {busy ? (
              <>
                <Loader2 size={14} className="add-item-spin" /> Working…
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
